import useInputState from "../../../hooks/useInputState";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { AddBankAccount, RemoveBankAccount } from "./mutation";
import { GetBankAccount } from "../queries";
import { isNumeric } from "validator";
import DefaultToaster from "../../../atoms/DefaultToaster";
import { Intent } from "@blueprintjs/core";
import useMaskedInputState from "../../../hooks/useMaskedInputState";
import { useState } from "react";
import useTutorialsState from "../../../hooks/useTutorialsState";
import useTagAccount from "../../../hooks/useTagAccount";

export const useChangeProfileBankInfo = () => {
  const initialState = {
    error: "",
    isRequesting: false,
    isBusiness: false,
    routing: "",
    accountNumber: "",
    fullName: "",
    removeAlertOpen: false,
    addAccount: false,
    removeAccount: false
  };

  const [error, setError] = useState(initialState.error);
  const [isRequesting, setIsRequesting] = useState(initialState.isRequesting);
  const [isBusiness, setIsBusiness] = useInputState(initialState.isBusiness);
  const numberOnly = /^[0-9\b]+$/;
  const [routing, setRouting] = useMaskedInputState(initialState.routing, {
    regex: numberOnly,
    maxLength: 9
  });
  const [accountNumber, setAccountNumber] = useMaskedInputState(
    initialState.accountNumber,
    { regex: numberOnly, maxLength: 16 }
  );
  const [removeAlertOpen, setRemoveAlertOpen] = useState(
    initialState.removeAccount
  );
  const [addAccount, setAddAccount] = useState(initialState.addAccount);
  const [removeAccount, setRemoveAccount] = useState(
    initialState.removeAccount
  );
  const [fullName, setFullName] = useInputState(initialState.fullName);
  const { data, loading } = useQuery(GetBankAccount);
  const bankInfo = data && data.accounts ? data.accounts[0] : [];

  const [addBankAccount] = useMutation(AddBankAccount, {
    refetchQueries: [
      {
        query: GetBankAccount
      }
    ]
  });
  const [removeBankAccount] = useMutation(RemoveBankAccount, {
    refetchQueries: [
      {
        query: GetBankAccount
      }
    ]
  });

  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();

  async function removeExistingAccount() {
    if (!isRequesting) {
      await removeBankAccount();
      DefaultToaster.show({
        message: "Bank Account removed!",
        icon: "tick",
        intent: Intent.SUCCESS
      });
      setRemoveAlertOpen(false);
    }
  }

  async function saveBankAccount() {
    let variables = {
      bankInfo: {
        accountHolderName: fullName,
        accountRoutingNumber: routing,
        accountNumber: accountNumber,
        isBusinessAccount: isBusiness
      }
    };
    await addBankAccount({ variables });

    const hasAddedBankAccount = getHasCompletedTutorial("Add Bank Account");

    if (!hasAddedBankAccount) {
      await completeTutorial({
        variables: {
          name: "Add Bank Account",
          version: 1
        }
      });

      await tagAccount("Bank Account Info Provided");
    }

    resetForm();
    DefaultToaster.show({
      message: "Bank Account added!",
      icon: "tick",
      intent: Intent.SUCCESS
    });
  }

  function resetForm() {
    setRouting(initialState.routing);
    setAccountNumber(initialState.accountNumber);
    setFullName(initialState.fullName);
    setAddAccount(initialState.addAccount);
    setIsRequesting(initialState.isRequesting);
  }

  const addNewAccount = async () => {
    setError(undefined);
    setIsRequesting(true);
    const onError = (message) => {
      setError({ message });
      setIsRequesting(false);
    };
    try {
      if (!fullName) {
        onError("Full Name cannot be blank.");
      } else if (!routing || !isNumeric(routing) || routing.length !== 9) {
        onError("The routing number you entered is incorrect.");
      } else if (
        !accountNumber ||
        !(accountNumber.length > 4 && accountNumber.length < 17) ||
        !isNumeric(accountNumber)
      ) {
        onError("The account number you entered is incorrect.");
      } else {
        await saveBankAccount();
      }
    } catch (err) {
      onError(
        err.networkError.result.errors[0].message
          .split(":")
          .slice(-1)
          .join(" ")
      );
    }
  };

  return {
    error,
    routing,
    accountNumber,
    fullName,
    setFullName,
    setRouting,
    setAccountNumber,
    isRequesting,
    setIsRequesting,
    isBusiness,
    setIsBusiness,
    addNewAccount,
    removeExistingAccount,
    data,
    loading,
    removeAlertOpen,
    setRemoveAlertOpen,
    addAccount,
    setAddAccount,
    removeAccount,
    setRemoveAccount,
    bankInfo,
    resetForm
  };
};
